import { useState, type MouseEvent, type RefObject } from 'react'
import { THUMB_BOX_CLASS } from './layout'
import { Copy, Trash2 } from 'lucide-react'
import { cn, MediaStatusChip } from '@teach/ui'
import type { Block } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import { typeIcon, typeMeta } from './blockTypeLabels'
import { getTypeChip } from '@/features/editor/constants/blockTypeColors'
import { PhoneCanvas } from '@/features/editor/components/blocks/previews/PhoneCanvas'
import { renderBlockPreview } from '@/features/editor/components/blocks/previews/renderBlockPreview'
import { StrictLangContext } from '@/features/editor/components/blocks/previews/design/strictLangContext'
import { PreviewThumbnailContext } from '@/features/editor/components/blocks/previews/thumbnailContext'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import { ThumbSettingsButton } from './ThumbSettingsButton'
import { screenDeleteConfirm } from './screenDeleteConfirm'
import { LOCK_REASON_TEXT } from './lockReasonText'
import type { BlockLockReason } from './useBlocksInRun'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: Block
  index: number
  selected: boolean
  isCourseAuthor: boolean
  onSelect: () => void
  onDelete: () => void
  /** Копия ЭТОГО экрана — встаёт сразу за ним (`handleDuplicate`). */
  onDuplicate: () => void
  /** Копировать нельзя: идёт создание, нет прав, курс на проверке. */
  duplicateDisabled: boolean
  duplicateTitle?: string
  /** п.10: Ctrl/Cmd-клик по миниатюре — добавляет/убирает блок из набора
   *  мультивыбора (`useEditScope`, область «Выбранные»), не двигая якорь. */
  onToggleSelect: () => void
  /** Блок в наборе мультивыбора — отметка ОТДЕЛЬНАЯ от `selected` (якорь один). */
  multiSelected: boolean
  contentLang: ContentLang
  /** Блок занят прогоном, пишущим в блоки (`useBlocksInRun`). */
  inRun: boolean
  /** Чем именно занят: у правки блок переписывается прямо сейчас, у перевода
   *  список блоков неизвестен (§7.5) — «переписывается» соврало бы про
   *  причину. Тот же словарь причин (`LOCK_REASON_TEXT`), что у `BlockRunNotice`
   *  и `BlockPanelHeader`. */
  lockReason: BlockLockReason
  innerRef?: RefObject<HTMLDivElement>
}

export const BlockThumbnail = ({
  block,
  index,
  selected,
  isCourseAuthor,
  onSelect,
  onDelete,
  onDuplicate,
  duplicateDisabled,
  duplicateTitle,
  onToggleSelect,
  multiSelected,
  contentLang,
  inRun,
  lockReason,
  innerRef,
}: Props) => {
  const m = useMessages(messages)
  const TypeIcon = typeIcon(block)
  const { label, chipId } = typeMeta(block)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const deleteConfirm = screenDeleteConfirm(index, label)

  const handleClick = (e: MouseEvent<HTMLDivElement>) =>
    e.ctrlKey || e.metaKey ? onToggleSelect() : onSelect()

  return (
    <div
      ref={innerRef}
      data-testid="block-thumbnail"
      onClick={handleClick}
      className={cn(
        // Ref SortableBlockThumb: ring-offset selection (no border). The ring is
        // OUTSET (ring-offset) so the strip must reserve vertical padding for it
        // (BlockStrip py-*); no scale — a scaled card overflows the strip.
        THUMB_BOX_CLASS,
        'group relative shrink-0 cursor-pointer rounded transition-all duration-200',
        // Наведение — карточка чуть приподнимается (а не увеличивается: см. выше).
        'hover:-translate-y-1',
        // Копия ещё создаётся на сервере (временный отрицательный id) — видна и выделена
        // сразу, но нажимать на неё нечего, пока не пришёл настоящий блок.
        block.id < 0 ? 'pointer-events-none' : null,

        // п.10: отметка мультивыбора — рамка САМОЙ карточки, а не значок в
        // углу. Миниатюра при футере `h-40` шириной 61.6px: угловой бейдж 20px
        // безусловно наезжал на типовой чип и скрывал иконку типа (при
        // двузначном номере — почти целиком), а свободных углов на ней нет —
        // верх занят чипом и корзинкой, низ плашкой прогона. `outline` (вплотную
        // к краю), а не `ring`: якорь панели уже носит `ring` со отступом
        // наружу, и разные свойства с разной геометрией читаются раздельно,
        // когда блок одновременно якорь и отмечен.
        multiSelected ? 'outline outline-2 outline-success' : null,
      )}
    >
      {/* Выделение и наведение — одним контуром вокруг карточки ВМЕСТЕ с язычком
          (его видимая часть — 20px над карточкой). */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 -top-5 z-20 rounded-lg transition-shadow duration-200',
          selected
            ? 'ring-2 ring-brand ring-offset-2 ring-offset-background'
            : 'group-hover:ring-2 group-hover:ring-brand/50',
        )}
      />
      {/* Без своей заливки: серый фон проступал полоской между язычком и блоком. */}
      <div className="relative z-10 h-full w-full overflow-hidden rounded-md">
        {/* The strip thumbnail is a scaled-down FULL block render (ref parity), NOT
            a separate crude "mini" — PhoneCanvas zooms the 390×760 render to fit. */}
        <div className="pointer-events-none h-full w-full">
          <PhoneCanvas viewport="thumbnail" fit="width">
            {/* Миниатюра = холст: тот же язык правки, без подстановки второго. */}
            <StrictLangContext.Provider value>
              <PreviewThumbnailContext.Provider value>
                {renderBlockPreview(block, contentLang)}
              </PreviewThumbnailContext.Provider>
            </StrictLangContext.Provider>
          </PhoneCanvas>
        </div>
      </div>

      {/* Номер и тип блока — «язычок» НАД карточкой на всю её ширину: на самой
          миниатюре он закрывал содержимое. Место под него — `pt-8` у ленты. */}
      <div
        className={cn(
          // Язычок встык с верхним краем карточки (её верхние углы прямые): заход
          // под карточку просвечивал в долю пикселя справа, где холст масштаба
          // не дотягивает до края.
          // Нижние 6px — под скруглёнными верхними углами карточки (масштаб у
          // миниатюры точный, щелей в долю пикселя больше нет).
          'pointer-events-none absolute inset-x-0 bottom-[calc(100%-6px)] z-0 flex h-[26px] items-start justify-center gap-1 rounded-t-lg pt-[3px] text-[11px] font-bold',
          getTypeChip(chipId),
        )}
      >
        {index + 1}
        <TypeIcon className="h-3.5 w-3.5" />
      </div>

      {/* Прогон занял блок — та же манера, что у типового чипа (index+тип), но
          статус-чип поверх медиа (MediaStatusChip). Глагол честный: перевод
          НЕ обещает, что пишет именно в этот блок. Проверку курса на каждой
          миниатюре не повторяем (решение заказчика): о ней говорит окно
          публикации и погашенные кнопки. Перевод — тоже (решение заказчика):
          о нём говорит меню «Перевод» в шапке. */}
      {inRun && lockReason !== 'review' && lockReason !== 'translation' ? (
        <div className="pointer-events-none absolute inset-x-1.5 bottom-1.5 z-10 flex justify-center">
          <MediaStatusChip variant="warning" icon={LOCK_REASON_TEXT[lockReason].icon}>
            {LOCK_REASON_TEXT[lockReason].chip}
          </MediaStatusChip>
        </div>
      ) : null}

      {/* Копировать — справа внизу по наведению: копия встаёт сразу за этим экраном (с `lg`). */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDuplicate()
        }}
        disabled={duplicateDisabled}
        title={duplicateTitle ?? m.duplicateScreen}
        aria-label={m.duplicateScreen}
        className="absolute bottom-1 hidden lg:block right-1.5 z-10 rounded-full border border-foreground/30 bg-background/35 p-1 text-foreground opacity-0 shadow-md backdrop-blur-[3px] transition hover:bg-background/55 group-hover:opacity-100 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-0 disabled:group-hover:opacity-50"
      >
        <Copy className="h-3 w-3" />
      </button>
      {/* Уже `lg` на месте копии — настройки экрана (шторка). */}
      <ThumbSettingsButton onSelect={onSelect} />

      {/* Удалить — слева внизу по наведению; чип номера и типа сверху остаётся. */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          setConfirmOpen(true)
        }}
        // C-1: пока прогон переписывает блок, удалять его нельзя — иначе
        // подпись «переписывается» обещает недоступность, которой нет, а
        // автор теряет уже оплаченную перепись безвозвратно.
        disabled={!isCourseAuthor || inRun}
        title={
          inRun ? LOCK_REASON_TEXT[lockReason].thumbTitle : !isCourseAuthor ? m.noRights : undefined
        }
        // Уже `lg` корзины нет: она стояла в 6px от шестерёнки настроек, и её нажимали вместо
        // неё. На телефоне экран удаляют из шапки шторки настроек.
        className="absolute bottom-1 left-1.5 z-10 hidden rounded-full border border-destructive/60 lg:block bg-destructive/45 p-1 text-destructive-foreground opacity-0 shadow-md backdrop-blur-[3px] transition hover:bg-destructive/65 group-hover:opacity-100 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-0 disabled:group-hover:opacity-50"
        aria-label={m.a11yDeleteBlock}
      >
        <Trash2 className="h-3 w-3" />
      </button>

      {/* Окно рендерится порталом, но события React всплывают по дереву
          КОМПОНЕНТОВ: клик «Удалить» долетал до миниатюры, и её `onSelect`
          после удаления выделял уже удалённый блок — панель падала на урок
          вместо соседа слева (пойман на прогоне перед бетой). */}
      <div className="contents" onClick={(e) => e.stopPropagation()}>
        <DeleteConfirmDialog
          open={confirmOpen}
          title={deleteConfirm.title}
          description={deleteConfirm.description}
          onOpenChange={setConfirmOpen}
          onConfirm={onDelete}
        />
      </div>
    </div>
  )
}
